import { trackReferralRegistration } from '@/api/partnerstack/trackReferralRegistration';
import { fetchUserRolesAndPermissions } from '@/api/user';
import {
  parseSubscriptionType,
  selectedSubscriptionTypeAtom,
} from '@/atoms/subscription';
import {
  AccountModalNames,
  useAccountModals,
} from '@/components/Modals/Account';
import { SubscriptionStatusCode } from '@/components/Modals/CreateAccount/checkout/status-codes';
import { blockSafelistPromptForSession } from '@/components/PageDialogQueue/dialogues/SafelistChannelPrompt/useSafelistChannelPromptEligibility';
import {
  externalSubscriptionSuccessAtom,
  openSubscribeModalAtom,
  subscribeModalOpenAtom,
  subscribeModalSelectedPlanAtom,
} from '@/components/Modals/Subscribe/atoms';
import { toast } from '@music-vine/cadence/ui';
import { plans } from '@/fixtures/plans';
import { useAuthToken } from '@/hooks/state/useAuthToken';
import useConversionDetails from '@/hooks/analytics/useConversionDetails';
import {
  useQueryState,
  useQueryStateBoolean,
} from '@/hooks/state/useQueryState';
import { useReferralAlerts } from '@/hooks/useReferralAlerts';
import { useSettings } from '@/hooks/state/useSettings';
import { log, trackConversion } from '@/lib/utils';
import { useStripeOverlayContext } from '@/providers/StripeOverlayProvider';
import type { Role } from '@/types/auth';
import * as Sentry from '@sentry/nextjs';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { retryCheckSubscriptionStatus } from './retryCheckSubscriptionStatus';

/**
 * Allowed subscription plan names for validation
 * Used to validate plan names from URL parameters
 */
const allowedPlans = [
  'Free',
  'Essentials',
  'Creator',
  'Pro',
  'Business',
] as const;

type AllowedPlan = (typeof allowedPlans)[number];

/**
 * Utility: Capitalize the first letter of a string
 * Used for formatting plan names from URL parameters
 */
function capitalizeFirstLetter(str: string | undefined): string | null {
  if (!str) {
    return null;
  }

  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Type guard: Check if a plan name is valid
 * Ensures plan names from URL parameters are recognized subscription types
 */
function isAllowedPlan(plan: string | null): plan is AllowedPlan {
  return allowedPlans.includes(plan as AllowedPlan);
}

/**
 * Critical Helper: Check if user has a paid subscription role
 *
 * This function determines if a user has moved from Free to a paid plan
 * by checking their roles against our plan definitions.
 *
 * @param roles - User's current roles (can be single role or array)
 * @returns true if user has any paid plan role (non-Free)
 *
 * Used in the polling loop to determine when the backend has successfully
 * processed the Stripe webhook and updated the user's account.
 */
function hasPaidSubscriptionRole(roles: Role | Role[]): boolean {
  const roleArray = Array.isArray(roles) ? roles : [roles];
  const paidPlanNames = plans
    .filter((plan) => plan.name !== 'Free')
    .map((plan) => plan.name as string);
  return roleArray.some((role) => paidPlanNames.includes(role as string));
}

/**
 * Flow Overview:
 * 1. User completes Stripe Checkout → redirected back with query params
 * 2. Hook detects 'success=true' in URL
 * 3. Shows loading overlay while processing
 * 4. Checks if Stripe subscription is active
 * 5. Waits for backend webhook processing and role updates
 * 6. Refreshes tokens and UI state
 * 7. Shows success modal
 *
 * The key insight: Stripe webhook processing is async and can take time.
 * We must wait for the user's roles to be updated before refreshing tokens.
 */
export const useStripeCheckoutResponse = () => {
  const router = useRouter();
  const { setCurrentModal } = useAccountModals();

  // Stripe overlay for showing progress
  const { setShowStripeOverlay, setVariantStripeOverlay } =
    useStripeOverlayContext();

  // Auth token refresh for getting updated permissions
  const { refetchAuthToken } = useAuthToken();

  // Query parameters from Stripe redirect
  // These tell us if the checkout was successful, canceled, and what plan.
  //
  // Read the flags from router.query, NOT the location atom: the atom syncs on
  // routeChangeComplete, one tick after router.pathname updates, so leaving
  // /subscribe?success=true commits one render of (pathname '/music',
  // success=true) — stale enough to wake the overlay + modal the /subscribe
  // bail below exists to prevent. router.query moves atomically with pathname.
  // The atom-backed setters are kept for clearing the params from the URL.
  const [, setSuccess] = useQueryStateBoolean('success');
  const [, setCanceled] = useQueryStateBoolean('canceled');
  const success = router.query.success === 'true';
  const canceled = router.query.canceled === 'true';
  const [plan] = useQueryState('plan', {
    parse: (value: string) => parseSubscriptionType.parse(value) ?? undefined,
    serialize: parseSubscriptionType.serialize,
  });
  const [length] = useQueryState('length');

  // Global state management for success flow
  const setExternalSubscriptionSuccess = useSetAtom(
    externalSubscriptionSuccessAtom
  );
  const openSubscribeModal = useSetAtom(openSubscribeModalAtom);

  log('query params', { success, canceled, plan, length });

  // Hooks for post-subscription actions
  const { triggerReferralAlerts } = useReferralAlerts();
  const { settings, invalidateSettingsAsync, getUserIdAsync } = useSettings();

  // Conversion tracking data
  const {
    subscriptionType,
    subscriptionLength,
    asPath,
    currencyCode,
    value,
    paymentType,
  } = useConversionDetails();

  // Validate and get subscription length with fallback
  const validatedSubscriptionLength = useMemo<'Annual' | 'Monthly'>(() => {
    const normalizedLength = length?.toLowerCase();

    if (normalizedLength === 'annual') {
      return 'Annual';
    }
    if (normalizedLength === 'monthly') {
      return 'Monthly';
    }

    if (normalizedLength) {
      Sentry.captureMessage(`Unexpected subscription length value: ${length}`, {
        level: 'warning',
        tags: { source: 'useStripeCheckoutResponse' },
      });
    }

    return subscriptionLength;
  }, [length, subscriptionLength]);

  const selectedSubscriptionType = useAtomValue(selectedSubscriptionTypeAtom);

  const setSelectedPlan = useSetAtom(subscribeModalSelectedPlanAtom);
  const [subscribeModalOpen, setSubscribeModalOpen] = useAtom(
    subscribeModalOpenAtom
  );

  // Track whether we've already processed the checkout response
  const hasProcessedResponse = useRef(false);
  const prevQueryParams = useRef<{
    success: boolean;
    canceled: boolean;
  }>({
    success: false,
    canceled: false,
  });

  // Create a stable reference to invalidateSettingsAsync to prevent infinite loops
  const invalidateSettingsRef = useRef(invalidateSettingsAsync);

  invalidateSettingsRef.current = invalidateSettingsAsync;

  const clearQueryParams = useCallback(async () => {
    // We don't clear the plan so that the tracking can still work
    await Promise.all([setSuccess(false), setCanceled(false)]);
  }, [setCanceled, setSuccess]);

  /**
   * Handle pending state with overlay
   *
   * This is the core function that solves the race condition between
   * Stripe webhook processing and frontend token refresh.
   *
   * Step-by-step process:
   * 1. Show loading overlay to user
   * 2. Check if Stripe subscription is active (quick check)
   * 3. Poll backend until user's roles are updated
   * 4. Only then refresh tokens and UI state
   * 5. Show success modal
   *
   * Why this is necessary:
   * - Stripe webhook processing is asynchronous
   * - Backend may take seconds to update user roles
   * - If we refresh tokens too early, user still appears as "Free"
   * - This causes "Failed to obtain asset authorization" errors
   */
  const handlePendingStateInOverlay = useCallback(async () => {
    // Step 1: Show loading overlay
    setShowStripeOverlay(true, { variant: 'pending' });

    // Step 2: Verify Stripe subscription is active
    // This checks Stripe's side but doesn't guarantee our backend is updated
    const userStatusCode = await retryCheckSubscriptionStatus({
      maxRetries: 6,
      throwOnError: false,
      userId: await getUserIdAsync(),
    });

    if (userStatusCode === SubscriptionStatusCode.ACTIVE) {
      setExternalSubscriptionSuccess(true);

      // Step 3: Wait for backend webhook processing
      // Poll until user's roles show they have a paid subscription
      let attemptsRemaining = 10; // Up to 20 seconds of polling
      let userUpdated = false;

      log(
        'Waiting for backend to process subscription and update user account...'
      );

      // Polling loop: Check every 2 seconds for up to 20 seconds
      while (attemptsRemaining > 0 && !userUpdated) {
        // Wait before checking (give backend time to process webhook)
        await new Promise((resolve) => setTimeout(resolve, 2000));

        try {
          // Fetch fresh user roles directly from the API
          // This bypasses any caching and gives us real-time role data
          const userRolesData = await fetchUserRolesAndPermissions(
            settings?.user?.id
          );

          if (userRolesData?.role) {
            // Check if user now has a paid subscription role
            const isPaidUser = hasPaidSubscriptionRole(userRolesData.role);
            log(
              `Attempt ${11 - attemptsRemaining}: User roles: ${JSON.stringify(userRolesData.role)}, isPaidUser: ${isPaidUser}`
            );

            if (isPaidUser) {
              // SUCCESS! Backend has processed webhook and updated user roles
              userUpdated = true;
              log('✅ User account successfully updated with premium role!');
              break;
            }
          } else {
            log(`Attempt ${11 - attemptsRemaining}: No role data received`);
          }
        } catch (error) {
          log(
            'Error fetching fresh user roles during subscription check:',
            error
          );
        }

        attemptsRemaining--;
      }

      // Handle timeout case - backend took too long to update
      if (!userUpdated) {
        log(
          '⚠️ User account did not update to premium role within expected time - proceeding anyway'
        );

        // Log to Sentry for monitoring webhook processing delays
        Sentry.captureMessage(
          'User account did not update to premium role within expected time - proceeding anyway',
          {
            level: 'warning',
            tags: { source: 'useStripeCheckoutResponse' },
          }
        );
      }

      // Step 4: Refresh UI state and tokens
      // Now that we've confirmed (or timed out), update the frontend state

      // Invalidate settings cache so the UI shows updated subscription status
      await invalidateSettingsRef.current();

      // Refresh the auth token to get updated permissions/roles
      // This is critical for download authorization to work
      await refetchAuthToken();

      // The safelist prompt (DEV-5907) first appears in the user's next
      // session, not the one they subscribed in.
      blockSafelistPromptForSession();

      // Step 5: Trigger post-subscription actions and tracking

      // Trigger referral alerts for new checkout flow
      triggerReferralAlerts();

      try {
        // Track referral registration for analytics
        // Note: This may fire multiple times, backend handles deduplication
        trackReferralRegistration();
      } catch (error) {
        Sentry.captureException(error, {
          tags: {
            source: 'trackReferralRegistration',
          },
        });
      }

      // Track conversion for analytics and attribution
      void trackConversion({
        subscriptionType:
          // Prioritize plan from Stripe URL params as most reliable
          plan ?? selectedSubscriptionType ?? subscriptionType,
        subscriptionLength: validatedSubscriptionLength,
        paymentType,
        asPath,
        currencyCode,
        value,
      });

      // Step 6: Show success modal and clean up
      // Use setTimeout to ensure UI updates are processed
      setTimeout(() => {
        // Hide the loading overlay
        setShowStripeOverlay(false);

        // Show the success modal
        openSubscribeModal();

        // Clean up URL parameters after a brief delay
        setTimeout(() => {
          void clearQueryParams();
        }, 100);
      }, 0);
    } else {
      // Handle case where Stripe subscription check failed
      setVariantStripeOverlay('error');
      void clearQueryParams();
    }
  }, [
    setShowStripeOverlay,
    getUserIdAsync,
    refetchAuthToken,
    triggerReferralAlerts,
    plan,
    selectedSubscriptionType,
    subscriptionType,
    validatedSubscriptionLength,
    paymentType,
    asPath,
    currencyCode,
    value,
    setExternalSubscriptionSuccess,
    openSubscribeModal,
    clearQueryParams,
    setVariantStripeOverlay,
  ]);

  /**
   * Handle checkout response from URL parameters
   *
   * This function is triggered when we detect success=true or canceled=true
   * in the URL after the user returns from Stripe Checkout.
   *
   * Flow:
   * 1. Validate user is authenticated
   * 2. Handle canceled subscriptions (show modal to retry)
   * 3. Handle successful subscriptions (trigger overlay processing)
   */
  const processCheckoutResponse = useCallback(() => {
    log('processCheckoutResponse', { success, canceled, plan });

    // Prevent duplicate processing of the same checkout response
    hasProcessedResponse.current = true;

    // Exit if no relevant query parameters
    if (!(success || canceled)) {
      return;
    }

    // Ensure user is logged in before processing subscription
    if (!settings?.user?.is_authenticated) {
      setCurrentModal(AccountModalNames.Login);
      void clearQueryParams();
      return;
    }

    // Handle canceled checkout - show subscribe modal to retry
    if (canceled || !success) {
      toast.info('Account upgrade cancelled');

      // Restore the selected plan in the modal
      const capitalizedPlan = capitalizeFirstLetter(plan);
      setSelectedPlan(isAllowedPlan(capitalizedPlan) ? capitalizedPlan : null);
      setSubscribeModalOpen(true);
      void clearQueryParams();
      return;
    }

    // Handle successful checkout
    if (success) {
      void handlePendingStateInOverlay();
    }
  }, [
    success,
    canceled,
    plan,
    settings?.user?.is_authenticated,
    setCurrentModal,
    clearQueryParams,
    setSelectedPlan,
    setSubscribeModalOpen,
    handlePendingStateInOverlay,
  ]);

  // Only process checkout response when we have query params and haven't processed yet
  useEffect(() => {
    // /subscribe is the new Stripe-Elements checkout page. It handles its own
    // success rendering inline (see SubscribePageContent) — the legacy overlay
    // polling here would race against it and fail with "Plan not found"
    // because the new subscription doesn't flow through the same backend path.
    const isOnNewCheckoutPage = router.pathname.startsWith('/subscribe');

    const shouldProcess =
      !(
        hasProcessedResponse.current ||
        subscribeModalOpen ||
        isOnNewCheckoutPage
      ) &&
      (success || canceled);

    if (shouldProcess) {
      processCheckoutResponse();
    }
  }, [
    success,
    canceled,
    subscribeModalOpen,
    processCheckoutResponse,
    router.pathname,
  ]);

  // Reset the processed flag when query params change (new checkout attempt)
  useEffect(() => {
    if (
      success !== prevQueryParams.current.success ||
      canceled !== prevQueryParams.current.canceled
    ) {
      hasProcessedResponse.current = false;
      prevQueryParams.current = { success, canceled };
    }
  }, [success, canceled]);

  return null;
};
